﻿import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import { setIsRefreshing } from "./retryRequestSlice";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

const hasLoginExpired = () => {
  dayjs.extend(utc);
  const refreshTokenExists = !!localStorage?.getItem("accessToken");
  const refreshTokenExpiryStr = localStorage?.getItem("refreshExpiry");

  const refreshTokenExpiry = dayjs(refreshTokenExpiryStr).utc(true);
  const utcNow = dayjs().utc(true);

  return !(refreshTokenExists && utcNow < refreshTokenExpiry);
};
// SHEP-304: In order to sync state between multiple tabs, local storage is used to maintain auth state. Redux is only used to sync to other components.
const authSlice = createSlice({
  name: "auth",
  initialState: {
    loggedIn: !hasLoginExpired(),
    refreshExpiry: localStorage.getItem("refreshExpiry") ?? null,
    error: null,
  },
  reducers: {
    setLoggedIn: (state, action) => {
      state.loggedIn = action.payload;
    },
    setRefreshExpiry: (state, action) => {
      const expiry = action.payload;
      if (expiry != null) {
        localStorage.setItem("refreshExpiry", expiry);
      } else {
        localStorage.removeItem("refreshExpiry");
      }
      state.refreshExpiry = expiry;
    },
    setError: (state, action) => {
      const { msg } = action.payload;
      state.error = msg;
    },
    setTenantName: (state, action) => {
      const { tenantName } = action.payload;
      if (tenantName != null) {
        localStorage.setItem("tenantName", tenantName);
      } else {
        localStorage.removeItem("tenantName");
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logOut.fulfilled, logOutAction)
      .addCase(logOut.rejected, (state, action) => {
        state.error = action.error.message;
        logOutAction(state);
      });
  },
});

const logOutAction = (state) => {
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshExpiry");
  localStorage.removeItem("tenantName");
  state.loggedIn = false;
  state.refreshExpiry = null;
};

export const logOut = createAsyncThunk("auth/logOut", async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (refreshToken) {
    return await axios.post("/login/logout", refreshToken);
  }
  return Promise.resolve();
});
export const setEnvironment = (params) => async (dispatch) => {
  const { refreshToken, tenantToken, tenantName } = params;

  await axios
    .put("/login/environment", {
      RefreshToken: refreshToken,
      TenantToken: tenantToken,
    })
    .then((res) => {
      localStorage.setItem("refreshToken", res.data.RefreshToken);
      localStorage.setItem("accessToken", res.data.AccessToken);
      dispatch(setTenantName({ tenantName: tenantName }));
      dispatch(setRefreshExpiry(res.data.RefreshExpiry));
      dispatch(setLoggedIn(true));
    })
    .catch(() => dispatch(logOut()));
};
export const refreshToken = () => async (dispatch) => {
  dispatch(setIsRefreshing(true));
  if (hasLoginExpired()) {
    dispatch(logOut());
    dispatch(setIsRefreshing(false));
    return false;
  }
  try {
    const response = await axios.post(
      "/login/refresh",
      localStorage.getItem("refreshToken"),
    );
    if (response.data !== undefined) {
      localStorage.setItem("refreshToken", response.data.RefreshToken);
      localStorage.setItem("accessToken", response.data.AccessToken);
      dispatch(setRefreshExpiry(response.data.RefreshExpiry));
      dispatch(setLoggedIn(true));
      dispatch(setIsRefreshing(false));
      return true;
    } else {
      dispatch(setError("Undefined token returned"));
      dispatch(logOut());
    }
  } catch (err) {
    dispatch(setError(err.message));
    dispatch(logOut());
  }
  dispatch(setIsRefreshing(false));
  return false;
};

export const { setLoggedIn, setRefreshExpiry, setError, setTenantName } =
  authSlice.actions;
export const selectSignInState = (state) => state.tempInfo.auth.loggedIn;
export const selectRefreshExpiry = (state) => state.tempInfo.auth.refreshExpiry;

export default authSlice.reducer;
